import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Plus, Trash2, Save, RotateCcw, AlertCircle, CheckCircle,
  ChevronDown, ChevronRight, ChevronLeft, X, Settings2, Info,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { useMachineSetupStore } from '../../stores/machine-setup-store';
import type {
  MachineTypeSettingDto,
  PresetAttributeViewDto,
  PersistedPresetAttribute,
  AddCustomMachineTypeRequest,
} from '../../types/machine-setup-types';
import attributeOptionsRaw from '../../data/attribute-options.json';

const ATTRIBUTE_OPTIONS = attributeOptionsRaw as Record<string, { codeId: number; label: string }[]>;

// ── Helpers ───────────────────────────────────────────────────────────────────

const emptyRow = (): PersistedPresetAttribute => ({ deviceAttributeId: 0, value: '', codeId: undefined, wizardVisible: false });

function toRows(attrs: PresetAttributeViewDto[]): PersistedPresetAttribute[] {
  return attrs.map((a) => ({
    deviceAttributeId: a.deviceAttributeId,
    value: a.value,
    codeId: a.codeId ?? undefined,
    wizardVisible: a.wizardVisible ?? false,
  }));
}

// Matches the group names in attribute_catalog.json (sourced from grid_attributes_states xlsx)
const CATEGORY_ORDER = [
  'General Information', 'Communication', 'Communication Ports', 'Ethernet / Wifi', 'Modem', 'Sim Card',
  'Card Readers', 'External Reader Configurations', 'NFC Config',
  'Payment', 'MDB', 'LCD', 'VPOS Touch', 'Language / Audio', 'Print Receipt Format',
  'EV Charger', 'EV Server',
  'Pulse/ccTalk', 'RS232 Control', 'Device Features', 'VMC', 'DEX/DDCMP',
  'Embedded', 'Embedded Advanced', 'Machine File Transfer', 'Temperature', 'Safe',
  'Agamento Config', 'JVMA Settings', 'Nova Market', 'Retail Payment App Config',
  'Retail SDK Config', 'Retail SPI Services', 'Triumph',
  'Other',
];

/** Groups attribute rows by category, preserving original row indices for correct state updates. */
function groupByCategory(
  rows: PersistedPresetAttribute[],
  attrCategories: Record<string, string>,
): { category: string; entries: { index: number; row: PersistedPresetAttribute }[] }[] {
  const map = new Map<string, { index: number; row: PersistedPresetAttribute }[]>();

  rows.forEach((row, index) => {
    const cat = attrCategories[row.deviceAttributeId?.toString()] ?? 'Other';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push({ index, row });
  });

  return [...map.entries()]
    .sort(([a], [b]) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map(([category, entries]) => ({ category, entries }));
}

// ── Attribute search combobox ─────────────────────────────────────────────────

interface AttrComboboxProps {
  value: number;
  attrNames: Record<string, string>;
  onChange: (id: number) => void;
  inputClass: string;
}

function AttrCombobox({ value, attrNames, onChange, inputClass }: AttrComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const ref               = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayValue = value > 0
    ? (attrNames[value.toString()] ? `${value} — ${attrNames[value.toString()]}` : String(value))
    : '';

  const q = query.trim().toLowerCase();
  const matches = Object.entries(attrNames)
    .filter(([id, name]) => !q || id.includes(q) || name.toLowerCase().includes(q))
    .sort(([a], [b]) => Number(a) - Number(b))
    .slice(0, 30);

  const handleSelect = (id: number) => {
    onChange(id);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        className={inputClass}
        value={open ? query : displayValue}
        placeholder="ID or name…"
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => setQuery(e.target.value)}
      />
      {open && (
        <ul className="absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-gray-200 bg-white text-sm shadow-lg">
          {matches.length === 0 && (
            <li className="px-3 py-2 text-gray-400">No matches</li>
          )}
          {matches.map(([id, name]) => (
            <li
              key={id}
              className="cursor-pointer px-3 py-1.5 hover:bg-primary/10"
              onMouseDown={() => handleSelect(Number(id))}
            >
              <span className="font-mono text-xs text-gray-500 mr-2">{id}</span>
              {name}
            </li>
          ))}
          {q && !isNaN(Number(q)) && Number(q) > 0 && !attrNames[q] && (
            <li
              className="cursor-pointer border-t border-gray-100 px-3 py-1.5 text-gray-500 hover:bg-primary/10"
              onMouseDown={() => handleSelect(Number(q))}
            >
              Use ID <span className="font-mono font-medium">{q}</span> (unknown attribute)
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ── Editable attribute row ────────────────────────────────────────────────────

interface RowProps {
  row: PersistedPresetAttribute;
  attrNames: Record<string, string>;
  onChange: (updated: PersistedPresetAttribute) => void;
  onDelete: () => void;
}

function AttributeRow({ row, attrNames, onChange, onDelete }: RowProps) {
  const td      = 'px-3 py-2 align-top';
  const inp     = 'w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring';
  const options = ATTRIBUTE_OPTIONS[String(row.deviceAttributeId)];

  return (
    <tr className={`border-b border-gray-100 hover:bg-gray-50 ${row.wizardVisible ? 'bg-blue-50/40' : ''}`}>
      <td className={td} style={{ width: 240 }}>
        <AttrCombobox
          value={row.deviceAttributeId}
          attrNames={attrNames}
          onChange={(id) => onChange({ ...row, deviceAttributeId: id, value: '', codeId: undefined })}
          inputClass={inp}
        />
      </td>
      <td className={td}>
        {options?.length ? (
          <select
            className={inp + ' bg-white'}
            value={row.codeId ?? ''}
            onChange={(e) => {
              const opt = options.find(o => o.codeId === Number(e.target.value));
              if (opt) onChange({ ...row, value: opt.label, codeId: opt.codeId });
            }}
          >
            <option value="">Select…</option>
            {options.map(o => (
              <option key={o.codeId} value={o.codeId}>{o.label}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            className={inp}
            value={row.value}
            onChange={(e) => onChange({ ...row, value: e.target.value })}
            placeholder={attrNames[row.deviceAttributeId?.toString()] || 'Enter value…'}
          />
        )}
      </td>
      <td className="px-3 py-2 text-center align-top w-24">
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded accent-primary cursor-pointer"
            checked={row.wizardVisible ?? false}
            onChange={(e) => onChange({ ...row, wizardVisible: e.target.checked })}
          />
          <span className="text-xs text-gray-500">Wizard</span>
        </label>
      </td>
      <td className="px-3 py-2 text-center align-top w-10">
        <button onClick={onDelete} className="text-red-400 hover:text-red-600 mt-0.5">
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

// ── Category section ──────────────────────────────────────────────────────────

interface CategorySectionProps {
  category: string;
  entries: { index: number; row: PersistedPresetAttribute }[];
  attrNames: Record<string, string>;
  onUpdate: (index: number, updated: PersistedPresetAttribute) => void;
  onDelete: (index: number) => void;
}

function CategorySection({ category, entries, attrNames, onUpdate, onDelete }: CategorySectionProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      <tr
        className="cursor-pointer select-none bg-gray-50 hover:bg-gray-100"
        onClick={() => setCollapsed((v) => !v)}
      >
        <td colSpan={4} className="px-3 py-1.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {category}
            <span className="ml-1 font-normal normal-case text-gray-400">({entries.length})</span>
          </div>
        </td>
      </tr>
      {!collapsed && entries.map(({ index, row }) => (
        <AttributeRow
          key={index}
          row={row}
          attrNames={attrNames}
          onChange={(updated) => onUpdate(index, updated)}
          onDelete={() => onDelete(index)}
        />
      ))}
    </>
  );
}

// ── Inline tooltip ────────────────────────────────────────────────────────────

function InfoTip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600 focus:outline-none"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        tabIndex={0}
        aria-label="More information"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {visible && (
        <span className="absolute left-5 top-0 z-50 w-64 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg leading-relaxed pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

// ── Add Machine Type modal ────────────────────────────────────────────────────

interface AddTypeModalProps {
  onClose: () => void;
  onAdd: (req: AddCustomMachineTypeRequest) => Promise<void>;
  existingHardwareModels: string[];
  defaultHardwareModel?: string;
}

// Maps hardware model display names to the category key used by the wizard (mirrors backend _hwModelToCategoryKey).
const HW_MODEL_TO_CATEGORY: Record<string, string> = {
  'VPOST':         'vpost',
  'Medias 4 Mini': 'media4_mini',
  'Medias 4':      'media4',
  'Medias 5':      'media5',
};

function deriveCategory(hwModel: string): string {
  return HW_MODEL_TO_CATEGORY[hwModel] ?? 'media4_mini';
}

function AddTypeModal({ onClose, onAdd, existingHardwareModels, defaultHardwareModel }: AddTypeModalProps) {
  const [form, setForm] = useState<AddCustomMachineTypeRequest>({
    key: '', displayName: '', machineTypeId: 0, machineModelId: 0,
    category: deriveCategory(defaultHardwareModel ?? ''),
    hardwareModel: defaultHardwareModel ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async () => {
    if (!form.key.trim())         return setError('Key is required.');
    if (!form.displayName.trim()) return setError('Display name is required.');
    setSaving(true);
    setError('');
    try {
      await onAdd(form);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add type.');
      setSaving(false);
    }
  };

  const inp = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-2 font-semibold text-gray-900">
            <Settings2 className="h-4 w-4 text-primary" />
            Add Machine Type
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1 flex items-center text-xs font-medium text-gray-600">
              Type Key
              <InfoTip text="Unique snake_case identifier used internally (e.g. medias4_outdoor). Cannot be changed after creation." />
            </label>
            <input
              type="text"
              className={inp}
              placeholder="e.g. medias4_outdoor"
              value={form.key}
              onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
            />
          </div>
          <div>
            <label className="mb-1 flex items-center text-xs font-medium text-gray-600">
              Display Name
              <InfoTip text="Human-readable name shown in the machine setup wizard and settings UI (e.g. Outdoor Single)." />
            </label>
            <input
              type="text"
              className={inp}
              placeholder="e.g. Outdoor Single"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            />
          </div>
          <div>
            <label className="mb-1 flex items-center text-xs font-medium text-gray-600">
              Hardware Model
              <InfoTip text="The hardware family this type belongs to (e.g. VPOST, Medias 4 Mini). Determines which group it appears under in the wizard and settings page." />
            </label>
            <input
              list="modal-hw-model-list"
              type="text"
              className={inp}
              placeholder="e.g. VPOST, Medias 4"
              value={form.hardwareModel ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, hardwareModel: e.target.value, category: deriveCategory(e.target.value) }))}
            />
            <datalist id="modal-hw-model-list">
              {[...new Set(['VPOST', 'Medias 4 Mini', 'Medias 4', 'Medias 5', ...existingHardwareModels])].map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 flex items-center text-xs font-medium text-gray-600">
                Machine Type ID
                <InfoTip text="Nayax Lynx MachineTypeID sent on machine creation (e.g. 555007 for EV meters, 996232251 for kiosks)." />
              </label>
              <input
                type="number"
                className={inp}
                placeholder="e.g. 555007"
                value={form.machineTypeId || ''}
                onChange={(e) => setForm((f) => ({ ...f, machineTypeId: Number(e.target.value) }))}
              />
            </div>
            <div>
              <label className="mb-1 flex items-center text-xs font-medium text-gray-600">
                Machine Model ID
                <InfoTip text="Nayax Lynx MachineModelID that identifies the specific hardware variant (e.g. 274951465337012 for VPOS Smart Single)." />
              </label>
              <input
                type="number"
                className={inp}
                placeholder="e.g. 274951465337012"
                value={form.machineModelId || ''}
                onChange={(e) => setForm((f) => ({ ...f, machineModelId: Number(e.target.value) }))}
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Add Type
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function MachineSetupSettingsPage() {
  const [types, setTypes]                   = useState<MachineTypeSettingDto[]>([]);
  const [attrNames, setAttrNames]           = useState<Record<string, string>>({});
  const [attrCategories, setAttrCategories] = useState<Record<string, string>>({});
  const [selectedKey, setSelectedKey]       = useState<string>('');
  const [selectedHwGroup, setSelectedHwGroup] = useState<string | null>(null);
  const [rows, setRows]                     = useState<PersistedPresetAttribute[]>([]);
  const [displayName, setDisplayName]         = useState<string>('');
  const [hardwareModel, setHardwareModel]   = useState<string>('');
  const [lynxTypeId, setLynxTypeId]         = useState<number>(0);
  const [lynxModelId, setLynxModelId]       = useState<number>(0);
  const [dirty, setDirty]                   = useState(false);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [loadError, setLoadError]           = useState<string | null>(null);
  const [saveStatus, setSaveStatus]         = useState<'idle' | 'ok' | 'error'>('idle');
  const [saveMessage, setSaveMessage]       = useState('');
  const [showAddType, setShowAddType]       = useState(false);
  const [deletingCustom, setDeletingCustom] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [typeToDelete, setTypeToDelete]     = useState<string | null>(null);

  const {
    loadTypeSettings,
    saveTypeSettings,
    addCustomMachineType,
    deleteCustomMachineType,
  } = useMachineSetupStore();

  // ── Load ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    setLoadError(null);

    loadTypeSettings()
      .then((resp) => {
        if (!resp.isSuccess) { setLoadError(resp.errorMessage ?? 'Failed to load settings'); return; }
        setTypes(resp.types);
        setAttrNames(resp.attributeNames);
        // Full category catalog from API — covers all known attributes, not just those in current type lists
        setAttrCategories(resp.attributeCategories ?? {});

        // Do not auto-select — user navigates via hardware model cards
      })
      .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, [loadTypeSettings]);

  // ── Tab switch ──────────────────────────────────────────────────────────────

  const selectType = (key: string) => {
    const type = types.find((t) => t.key === key);
    if (!type) return;
    setSelectedKey(key);
    setRows(toRows(type.attributes));
    setDisplayName(type.displayName ?? '');
    setHardwareModel(type.hardwareModel ?? '');
    setLynxTypeId(type.machineTypeId ?? 0);
    setLynxModelId(type.machineModelId ?? 0);
    setDirty(false);
    setSaveStatus('idle');
  };

  // ── Hardware group navigation ────────────────────────────────────────────────

  const hwGroups = useMemo(
    () => types.reduce<Record<string, MachineTypeSettingDto[]>>((acc, t) => {
      const hw = t.hardwareModel?.trim() || 'Other';
      (acc[hw] ??= []).push(t);
      return acc;
    }, {}),
    [types],
  );

  const handleBack = () => {
    const hw = types.find((t) => t.key === selectedKey)?.hardwareModel?.trim() || 'Other';
    setSelectedKey('');
    setRows([]);
    setDirty(false);
    setSaveStatus('idle');
    setSelectedHwGroup(hw);
  };

  // ── Row editing ─────────────────────────────────────────────────────────────

  const updateRow = (index: number, updated: PersistedPresetAttribute) => {
    setRows((prev) => prev.map((r, i) => (i === index ? updated : r)));
    setDirty(true);
    setSaveStatus('idle');
  };

  const deleteRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    setSaveStatus('idle');
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
    setDirty(true);
    setSaveStatus('idle');
  };

  const handleReset = () => {
    const type = types.find((t) => t.key === selectedKey);
    if (type) { setRows(toRows(type.attributes)); setDisplayName(type.displayName ?? ''); setHardwareModel(type.hardwareModel ?? ''); setLynxTypeId(type.machineTypeId ?? 0); setLynxModelId(type.machineModelId ?? 0); setDirty(false); setSaveStatus('idle'); }
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const invalid = rows.find((r) => !r.deviceAttributeId || r.deviceAttributeId <= 0);
    if (invalid) { setSaveStatus('error'); setSaveMessage('All rows must have a valid Attribute ID.'); return; }

    setSaving(true);
    setSaveStatus('idle');

    try {
      const resp = await saveTypeSettings(selectedKey, {
        attributes: rows,
        hardwareModel: hardwareModel || null,
        displayName: displayName || null,
        ...(selectedType?.isCustom && {
          machineTypeId:  lynxTypeId  || null,
          machineModelId: lynxModelId || null,
        }),
      });

      if (resp.isSuccess) {
        setSaveStatus('ok');
        setSaveMessage('Saved successfully.');
        setDirty(false);
        setTypes((prev) => prev.map((t) =>
          t.key === selectedKey
            ? {
                ...t, hasOverrides: true,
                displayName: displayName || t.displayName,
                hardwareModel: hardwareModel || null,
                ...(t.isCustom && { machineTypeId: lynxTypeId, machineModelId: lynxModelId }),
                attributes: rows.map((r) => ({
                  deviceAttributeId: r.deviceAttributeId,
                  attributeName: attrNames[r.deviceAttributeId.toString()] ?? `#${r.deviceAttributeId}`,
                  category: attrCategories[r.deviceAttributeId.toString()] ?? 'Other',
                  value: r.value,
                  codeId: r.codeId,
                  wizardVisible: r.wizardVisible ?? false,
                })),
              }
            : t
        ));
      } else {
        setSaveStatus('error');
        setSaveMessage(resp.errorMessage ?? 'Save failed.');
      }
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveMessage(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };


  // ── Add custom type ─────────────────────────────────────────────────────────

  const handleAddType = async (req: AddCustomMachineTypeRequest) => {
    const resp = await addCustomMachineType(req);
    if (!resp.isSuccess) throw new Error(resp.errorMessage ?? 'Failed to add type.');

    const normalizedKey = req.key.toLowerCase().replace(/\s+/g, '_');
    const newType: MachineTypeSettingDto = {
      key: normalizedKey,
      displayName: req.displayName,
      hasOverrides: false,
      isCustom: true,
      hardwareModel: req.hardwareModel ?? null,
      machineTypeId: req.machineTypeId ?? 0,
      machineModelId: req.machineModelId ?? 0,
      attributes: [],
    };
    setTypes((prev) => [...prev, newType]);
    setShowAddType(false);
    setSelectedKey(normalizedKey);
    setRows([]);
    setHardwareModel(req.hardwareModel ?? '');
    setLynxTypeId(req.machineTypeId ?? 0);
    setLynxModelId(req.machineModelId ?? 0);
    setSelectedHwGroup(null);
    setDirty(false);
    setSaveStatus('idle');
  };

  // ── Delete custom type ──────────────────────────────────────────────────────

  const handleDeleteType = () => {
    setTypeToDelete(selectedKey);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteType = async () => {
    if (!typeToDelete) return;
    setDeleteConfirmOpen(false);
    setSaveStatus('idle');
    setDeletingCustom(true);
    try {
      const resp = await deleteCustomMachineType(typeToDelete);
      if (!resp.isSuccess) { setSaveStatus('error'); setSaveMessage(resp.errorMessage ?? 'Delete failed.'); return; }

      const remaining = types.filter((t) => t.key !== typeToDelete);
      setTypes(remaining);
      setSelectedKey('');
      setRows([]);
      setSelectedHwGroup(null);
      setDirty(false);
      setSaveStatus('idle');
    } finally {
      setDeletingCustom(false);
      setTypeToDelete(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 gap-2 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading settings…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{loadError}</div>
      </div>
    );
  }

  const selectedType = types.find((t) => t.key === selectedKey);
  const groups = groupByCategory(rows, attrCategories);

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Machine Type Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure which static attributes are applied after machine creation for each machine type.
          Changes override the hardcoded defaults and take effect immediately for new machines.
        </p>
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 mt-3 text-sm text-gray-400 select-none">
          <span
            className={selectedHwGroup || selectedKey ? 'cursor-pointer hover:text-gray-700 transition-colors' : 'text-gray-700 font-medium'}
            onClick={() => { if (selectedHwGroup || selectedKey) { setSelectedKey(''); setRows([]); setDirty(false); setSaveStatus('idle'); setSelectedHwGroup(null); } }}
          >
            Machine Setup
          </span>
          {(selectedHwGroup || selectedType?.hardwareModel?.trim()) && (
            <>
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              <span
                className={selectedKey ? 'cursor-pointer hover:text-gray-700 transition-colors' : 'text-gray-700 font-medium'}
                onClick={() => { if (selectedKey) { setSelectedKey(''); setRows([]); setDirty(false); setSaveStatus('idle'); setSelectedHwGroup(selectedType?.hardwareModel?.trim() || selectedHwGroup); } }}
              >
                {selectedType?.hardwareModel?.trim() || selectedHwGroup}
              </span>
            </>
          )}
          {selectedType && (
            <>
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              <span className="text-gray-700 font-medium">{selectedType.displayName}</span>
            </>
          )}
        </nav>
      </div>

      {/* ── Level 1: Hardware model cards (no type selected, no group selected) ── */}
      {!selectedKey && !selectedHwGroup && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {Object.entries(hwGroups).map(([hw, groupTypes]) => (
            <button
              key={hw}
              onClick={() => setSelectedHwGroup(hw)}
              className="flex flex-col items-start p-4 rounded-xl border border-gray-200 bg-white hover:border-primary hover:shadow-sm transition-all text-left group"
            >
              <span className="font-semibold text-gray-900 group-hover:text-primary transition-colors">{hw}</span>
              <span className="text-xs text-gray-400 mt-0.5 mb-3">
                {groupTypes.length} type{groupTypes.length !== 1 ? 's' : ''}
                {groupTypes.some((t) => t.hasOverrides) && (
                  <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-yellow-400 align-middle" title="Has saved overrides" />
                )}
              </span>
              <div className="flex flex-wrap gap-1">
                {groupTypes.map((t) => (
                  <span key={t.key} className="text-xs bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5 text-gray-500">
                    {t.displayName}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── Level 2: Types within selected hardware group ── */}
      {!selectedKey && selectedHwGroup && (
        <div className="mb-8">
          <button
            onClick={() => setSelectedHwGroup(null)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            All hardware
          </button>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{selectedHwGroup}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(hwGroups[selectedHwGroup] ?? []).map((t) => (
              <button
                key={t.key}
                onClick={() => selectType(t.key)}
                className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-white hover:border-primary hover:shadow-sm transition-all text-left group"
              >
                <div>
                  <span className="font-medium text-gray-900 text-sm group-hover:text-primary transition-colors">
                    {t.displayName}
                  </span>
                  {t.isCustom && (
                    <span className="ml-1 text-[10px] text-primary/60">✦</span>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t.hasOverrides ? 'Has saved overrides' : 'Using defaults'}
                  </p>
                </div>
                {t.hasOverrides && (
                  <span className="h-2 w-2 rounded-full bg-yellow-400 shrink-0 ml-2" title="Has saved overrides" />
                )}
              </button>
            ))}
            <button
              onClick={() => setShowAddType(true)}
              className="flex flex-col items-center justify-center p-4 rounded-xl border border-dashed border-gray-300 hover:border-primary hover:text-primary text-gray-400 transition-all"
            >
              <Plus className="h-4 w-4 mb-1" />
              <span className="text-sm">New model</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Level 3: Attribute editor ── */}
      {selectedType && (
        <>
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {selectedType.hardwareModel || 'Other'} / {selectedType.displayName}
          </button>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setDirty(true); setSaveStatus('idle'); }}
                className="text-base font-semibold text-gray-900 border-0 border-b border-transparent hover:border-gray-300 focus:border-primary focus:outline-none focus:ring-0 px-0 py-0 bg-transparent w-full max-w-xs"
                placeholder="Display name…"
              />
              <p className="text-xs text-gray-400 mt-0.5">
                {selectedType.isCustom && <span className="mr-1.5 text-primary/70">Custom type ·</span>}
                {selectedType.hasOverrides
                  ? 'Using saved overrides — defaults from MachineTemplates are not applied.'
                  : 'Showing defaults from MachineTemplates — no saved overrides yet.'}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-500 shrink-0">Hardware Model</label>
                  <input
                    list="hw-model-list"
                    type="text"
                    className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-ring w-40"
                    placeholder="e.g. VPOST, Medias 4"
                    value={hardwareModel}
                    onChange={(e) => { setHardwareModel(e.target.value); setDirty(true); setSaveStatus('idle'); }}
                  />
                  <datalist id="hw-model-list">
                    {[...new Set([
                      'VPOST', 'Medias 4 Mini', 'Medias 4', 'Medias 5',
                      ...types.map((t) => t.hardwareModel).filter((v): v is string => Boolean(v)),
                    ])].map((v) => <option key={v} value={v} />)}
                  </datalist>
                </div>
                {selectedType.isCustom && (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-gray-500 shrink-0 flex items-center">
                        Machine Type ID
                        <InfoTip text="Nayax Lynx MachineTypeID sent on machine creation (e.g. 555007 for EV meters)." />
                      </label>
                      <input
                        type="number"
                        className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-ring w-32"
                        placeholder="e.g. 555007"
                        value={lynxTypeId || ''}
                        onChange={(e) => { setLynxTypeId(Number(e.target.value)); setDirty(true); setSaveStatus('idle'); }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-gray-500 shrink-0 flex items-center">
                        Machine Model ID
                        <InfoTip text="Nayax Lynx MachineModelID for the specific hardware variant (e.g. 274951465337012)." />
                      </label>
                      <input
                        type="number"
                        className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-ring w-44"
                        placeholder="e.g. 274951465337012"
                        value={lynxModelId || ''}
                        onChange={(e) => { setLynxModelId(Number(e.target.value)); setDirty(true); setSaveStatus('idle'); }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedType.isCustom && (
                <button
                  onClick={handleDeleteType}
                  disabled={deletingCustom}
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600"
                  title="Delete this custom type"
                >
                  {deletingCustom ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Delete type
                </button>
              )}
              {dirty && (
                <button onClick={handleReset} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
              )}
              <Button onClick={handleSave} disabled={saving || !dirty} className="flex items-center gap-1.5">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </div>
          </div>

          {/* Save / error status */}
          {saveStatus !== 'idle' && (
            <div className={[
              'mx-6 mt-4 flex items-center gap-2 rounded-md px-3 py-2 text-sm',
              saveStatus === 'ok'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700',
            ].join(' ')}>
              {saveStatus === 'ok' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              {saveMessage}
            </div>
          )}

          {/* Attributes table */}
          <div className="p-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="px-3 pb-2 font-medium text-gray-600">
                    Attribute
                    <span className="block text-xs font-normal text-gray-400">search by ID or name</span>
                  </th>
                  <th className="px-3 pb-2 font-medium text-gray-600">Value</th>
                  <th className="px-3 pb-2 w-24 font-medium text-gray-600 text-center">
                    Show in wizard
                    <span className="block text-xs font-normal text-gray-400">user can edit</span>
                  </th>
                  <th className="px-3 pb-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-400">
                      No attributes configured — add rows below or save an empty list to clear all defaults.
                    </td>
                  </tr>
                )}
                {groups.map(({ category, entries }) => (
                  <CategorySection
                    key={category}
                    category={category}
                    entries={entries}
                    attrNames={attrNames}
                    onUpdate={updateRow}
                    onDelete={deleteRow}
                  />
                ))}
              </tbody>
            </table>

            <button
              onClick={addRow}
              className="mt-4 flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <Plus className="h-4 w-4" />
              Add attribute
            </button>

          </div>
        </div>
        </>
      )}

      {showAddType && (
        <AddTypeModal
          onClose={() => setShowAddType(false)}
          onAdd={handleAddType}
          existingHardwareModels={[...new Set(types.map((t) => t.hardwareModel).filter(Boolean))] as string[]}
          defaultHardwareModel={selectedHwGroup ?? undefined}
        />
      )}

      <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => !open && setDeleteConfirmOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete machine type?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteType}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
